import { formatCount } from "../../services/adsApi";
import { formatDate } from "../../services/boostApi";

// =====================================================
// MY CAMPAIGNS — the advertiser's campaign dashboard.
//
// Every figure comes from the server: status, minimum target, delivered
// impressions, actual duration and maximum duration. The STATUS is decided
// by the server from the campaign's real state (review, payment, delivery,
// admin action) — this screen only explains it. A campaign ends when the
// minimum target is reached (Completed) OR the maximum duration runs out
// (Expired), whichever happens first.
//
// ANALYTICS — aggregate numbers only, computed by the server from recorded
// events: impressions (qualifying displays), unique reach (distinct people
// reached), clicks, CTR and, for video ads, starts / views / completions.
// No viewer is ever identified here.
// =====================================================

const STATUS_NOTES = {
  draft: "Saved as a draft. Submit it for review when it is ready.",
  pending_review: "Submitted — waiting for admin review. You can pay once it is approved.",
  rejected: "This campaign was not approved.",
  approved: "Approved! Complete the payment to schedule your campaign.",
  payment_pending: "Payment started but not yet confirmed. If it did not go through, pay again below.",
  scheduled: "Payment confirmed. Your campaign starts on its start date.",
  active: "Running in the blog feed.",
  paused: "Paused by our team — it is not being shown right now.",
  completed: "Finished: the minimum estimated delivery target was reached.",
  expired: "Finished: the maximum campaign duration ended before the minimum target was reached.",
  removed: "Removed by our team. It will not be shown again.",
  cancelled: "Cancelled — it was not paid in time after approval.",
};

const PAYABLE = ["approved", "payment_pending"];

const pct = (n) => `${Number(n || 0)}%`;
const days = (n) => `${n} ${n === 1 ? "day" : "days"}`;

function Stat({ label, value }) {
  return (
    <div className="adw-stat">
      <span className="adw-stat-label">{label}</span>
      <span className="adw-stat-value">{value}</span>
    </div>
  );
}

function MyAdCampaigns({ campaigns, summary, typeLabel = {}, payingId, onPay, submittingId, onSubmitDraft }) {
  return (
    <section className="adw-card adw-dashboard">
      <h2 className="adw-card-heading">My Campaigns</h2>

      {summary && campaigns.length > 0 && (
        <div className="adw-stats" aria-label="Campaign analytics overview">
          <Stat label="Total Campaigns" value={formatCount(summary.totalCampaigns)} />
          <Stat label="Active Campaigns" value={formatCount(summary.activeCampaigns)} />
          <Stat label="Completed Campaigns" value={formatCount(summary.completedCampaigns)} />
          <Stat label="Total Spend" value={`₹${formatCount(summary.totalSpend)}`} />
          <Stat label="Total Impressions" value={formatCount(summary.totalImpressions)} />
          <Stat label="Unique Reach" value={formatCount(summary.uniqueReach)} />
          <Stat label="Total Clicks" value={formatCount(summary.totalClicks)} />
          <Stat label="CTR" value={pct(summary.ctr)} />
          <Stat label="Video Views" value={formatCount(summary.videoViews)} />
        </div>
      )}

      {!campaigns.length && <p className="adw-empty">You have not created any campaigns yet.</p>}

      {campaigns.map((c) => {
        const progress = Math.min(100, Math.round((c.deliveredImpressions / (c.minImpressionTarget || 1)) * 100));
        const a = c.analytics;
        const started = c.startedAt && c.durationUsedDays > 0;
        const statusText = c.status === "completed" ? "Campaign Completed" : c.statusLabel;
        return (
          <article className="adw-campaign" key={c.id}>
            <div className="adw-campaign-top">
              <div className="adw-campaign-name">
                <strong>{c.title}</strong>
                <span className="adw-campaign-type">
                  {typeLabel[c.adType] || c.adType} · ₹{c.price}
                </span>
              </div>
              <span className={`adw-status adw-status-${c.status}`}>{statusText}</span>
            </div>

            {STATUS_NOTES[c.status] && <p className="adw-campaign-note">{STATUS_NOTES[c.status]}</p>}
            {c.status === "rejected" && c.rejectionReason && (
              <p className="adw-campaign-note adw-campaign-reason">Reason: {c.rejectionReason}</p>
            )}
            {c.status === "removed" && c.removalReason && (
              <p className="adw-campaign-note adw-campaign-reason">Reason: {c.removalReason}</p>
            )}
            {c.status === "paused" && c.pauseReason && (
              <p className="adw-campaign-note adw-campaign-reason">Reason: {c.pauseReason}</p>
            )}
            {c.status === "draft" && c.securityCheck?.status === "failed" && (
              <p className="adw-campaign-note adw-campaign-reason">Website check: {c.securityCheck.summary}</p>
            )}
            {c.completionLabel && <p className="adw-campaign-note">{c.completionLabel}.</p>}

            <div className="adw-stats">
              <Stat label="Campaign Status" value={c.statusLabel} />
              <Stat label="Minimum Estimated Target" value={formatCount(c.minImpressionTarget)} />
              <Stat label="Delivered" value={formatCount(c.deliveredImpressions)} />
              <Stat label="Actual Duration" value={started ? days(c.durationUsedDays) : "Not started"} />
              <Stat label="Maximum Duration" value={days(c.maxDurationDays)} />
              {a && (
                <>
                  <Stat label="Unique Reach" value={formatCount(a.uniqueReach)} />
                  <Stat label="Clicks" value={formatCount(a.clicks)} />
                  <Stat label="CTR" value={pct(a.ctr)} />
                  {c.adType === "video" && (
                    <>
                      <Stat label="Video Starts" value={formatCount(a.videoStarts)} />
                      <Stat label="Video Views" value={formatCount(a.videoViews)} />
                      <Stat label="Video Completions" value={formatCount(a.videoCompletions)} />
                    </>
                  )}
                </>
              )}
            </div>

            <div className="adw-progress" aria-label={`${progress}% of the minimum target`}>
              <div className="adw-progress-fill" style={{ width: `${progress}%` }} />
            </div>

            <p className="adw-campaign-dates">
              Estimated Impressions: {formatCount(c.estimatedImpressions.min)}–{formatCount(c.estimatedImpressions.max)}
              {c.paymentStatus?.label && ` · Payment: ${c.paymentStatus.label}`}
              {!c.startedAt && c.requestedStartAt && ` · Requested start ${formatDate(c.requestedStartAt)}`}
              {c.startedAt && (started ? ` · Started ${formatDate(c.startedAt)}` : ` · Starts ${formatDate(c.startedAt)}`)}
              {c.completedAt
                ? ` · ${c.status === "expired" ? "Ended" : "Completed"} ${formatDate(c.completedAt)}`
                : c.endsAt
                ? ` · Ends by ${formatDate(c.endsAt)}`
                : ""}
              {` · ${c.categories.join(", ")} · ${c.targetCity || "All Cities"}`}
            </p>

            {PAYABLE.includes(c.status) && onPay && (
              <button type="button" className="adw-pay-btn" onClick={() => onPay(c)} disabled={Boolean(payingId)}>
                {payingId === c.id ? "Opening payment…" : `Pay now — ₹${c.price}`}
              </button>
            )}
            {c.status === "draft" && onSubmitDraft && (
              <button
                type="button"
                className="adw-pay-btn"
                onClick={() => onSubmitDraft(c)}
                disabled={Boolean(submittingId)}
              >
                {submittingId === c.id ? "Submitting…" : "Submit for Review"}
              </button>
            )}
          </article>
        );
      })}
    </section>
  );
}

export default MyAdCampaigns;
